import { useCallback, useEffect, useRef, useState } from "react";
import { apiAxios } from "../../utils/axios";
import { RequestName } from "./apiHookTypes";
import { retryAfterSeconds } from "utils/http/retryAfterSeconds";
import type {
  PortCheckData,
  PortCheckError,
  PortCheckErrorReason,
} from "components/Checkers/IpCheckerBlock/PortCheckBlock/types";

/**
 * On-demand `POST /port-check` (spec 2026-10-14-open-port-check, technical
 * design §3).
 *
 * Not routed through `useApiRequest`: that hook fires on mount / identity
 * change and keeps stale data across re-fires, while this is a button-driven
 * mutation that may run for up to ~20 s and must start from a clean state on
 * every press.
 */

/** The server's deadline is ~20 s; leave headroom for the network. */
export const PORT_CHECK_TIMEOUT_MS = 25000;

const KNOWN_REASONS: PortCheckErrorReason[] = [
  "invalid_ip",
  "plan_required",
  "rate_limited",
  "probes_unavailable",
  "edge_required",
  "port_check_unavailable",
];

/** Map an axios error onto the contract's reason codes. Anything the
 *  contract does not name (network error, timeout, 5xx without a reason)
 *  becomes `unknown` — the UI must not claim "no charge" for those. */
export const parsePortCheckError = (err: any): PortCheckError => {
  const body = err?.response?.data;
  const envelope = body?.reason ? body : body?.detail;
  const raw = envelope?.reason;
  const reason: PortCheckErrorReason =
    KNOWN_REASONS.indexOf(raw) !== -1 ? raw : "unknown";
  if (reason !== "rate_limited") return { reason };
  const headers = err?.response?.headers || {};
  return {
    reason,
    retryAfter:
      retryAfterSeconds(envelope?.retry_after) ??
      retryAfterSeconds(headers["retry-after"]),
  };
};

export const usePortCheck = (apiToken?: string) => {
  const [data, setData] = useState<PortCheckData | null>(null);
  const [error, setError] = useState<PortCheckError | null>(null);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (ip?: string) => {
      setLoading(true);
      setError(null);
      setData(null);

      const headers: Record<string, string> = { "X-Client-Source": "site" };
      // Same rule as useApiRequest: no token, no Authorization header.
      const defaultKey = process.env.REACT_APP_DEFAULT_API_KEY;
      if (apiToken && apiToken !== "undefined" && apiToken !== defaultKey) {
        headers.Authorization = `Bearer ${apiToken}`;
      }
      // Own-IP path: omit `ip` entirely, the server uses the IP it detected.
      const body = ip ? { ip } : {};

      try {
        const response = await apiAxios.post(`/${RequestName.PORT_CHECK}`, body, {
          headers,
          timeout: PORT_CHECK_TIMEOUT_MS,
        });
        const payload = response?.data;
        if (!mounted.current) return;
        if (payload?.status === "success" && payload?.data?.ports) {
          setData(payload.data as PortCheckData);
        } else {
          setError({ reason: "unknown" });
        }
      } catch (err) {
        if (mounted.current) setError(parsePortCheckError(err));
      } finally {
        if (mounted.current) setLoading(false);
      }
    },
    [apiToken]
  );

  return { data, error, loading, run };
};
